import { ReturnedReason } from "@/components/ReturnedReason";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, formatDateUK } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import DocumentPreview from "@/components/DocumentPreview";
import AuditTimeline from "@/components/AuditTimeline";
import ReviewPanel from "@/components/ReviewPanel";
import RecordFlags from "@/components/RecordFlags";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, FileText, User, Calendar, CreditCard,
  Store, Loader2, ShoppingCart, Receipt, CheckCircle2, Clock, XCircle, FileWarning,
} from "lucide-react";


const STATUS_INFO = {
  draft: { icon: Clock, color: "text-gray-500", bg: "bg-gray-50", label: "Draft" },
  submitted: { icon: Clock, color: "text-blue-600", bg: "bg-blue-50", label: "Submitted for Approval" },
  approved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", label: "Approved" },
  rejected: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", label: "Returned for correction" },
  voided: { icon: XCircle, color: "text-gray-600", bg: "bg-gray-100", label: "Voided" },
};

export default function ViewPurchasePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    api.get(`/purchases/${id}`).then((r) => { if (live) setData(r.data); }).catch(() => {
      if (!live) return;
      toast.error("Purchase not found or not accessible", { id: "not-found" });
      navigate("/submissions");
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, navigate, reload]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (!data) return null;

  const status = STATUS_INFO[data.status] || STATUS_INFO.draft;
  const StatusIcon = status.icon;

  return (
    <div className="space-y-5" data-testid="view-purchase-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/submissions")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40 hover:text-navy" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-heading font-bold text-navy">Purchase Details</h1>
      </div>

      {/* Status banner */}
      <div className={`${status.bg} rounded-xl p-4 flex items-center gap-3`}>
        <StatusIcon className={`w-5 h-5 ${status.color}`} />
        <div>
          <p className={`font-semibold text-sm ${status.color}`}>{status.label}</p>
          {data.submitted_by_name && <p className="text-xs text-navy/50 mt-0.5">Submitted by {data.submitted_by_name}</p>}
          {data.status === "rejected" && <ReturnedReason history={data.history} testid="purchase-returned-reason" />}
        </div>
        <p className="ml-auto font-mono text-lg font-bold text-navy">{formatGBP(data.total_amount)}</p>
      </div>

      {/* Transaction details */}
      <Card className="border-navy/5 shadow-none">
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold text-navy text-sm flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-gold" /> Transaction Information
          </h3>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-sm">
            {data.supplier && (
              <><span className="text-navy/50">Supplier</span><span className="text-navy font-medium">{data.supplier.name}</span></>
            )}
            {data.invoice_number && (
              <><span className="text-navy/50">Invoice No.</span><span className="text-navy">{data.invoice_number}</span></>
            )}
            {data.invoice_date && (
              <><span className="text-navy/50">Invoice Date</span><span className="text-navy">{formatDateUK(data.invoice_date) !== "-" ? formatDateUK(data.invoice_date) : data.invoice_date}</span></>
            )}
            <><span className="text-navy/50">Subtotal</span><span className="font-mono text-navy">{formatGBP(data.subtotal)}</span></>
            <><span className="text-navy/50">VAT</span><span className="font-mono text-navy">{formatGBP(data.vat_amount)}</span></>
            <><span className="text-navy/50">Total</span><span className="font-mono font-bold text-navy">{formatGBP(data.total_amount)}</span></>
          </div>
        </CardContent>
      </Card>

      {/* Payment info */}
      <Card className="border-navy/5 shadow-none">
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold text-navy text-sm flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-gold" /> Payment
          </h3>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-sm">
            <><span className="text-navy/50">Status</span><span className="text-navy capitalize" data-testid="purchase-settlement">{data.settlement ? { paid: "Paid", part_paid: "Part paid", unpaid: "Owed" }[data.settlement] : data.payment_status}</span></>
            {data.payment_method && (
              <><span className="text-navy/50">Method</span><span className="text-navy capitalize">{data.payment_method?.replace("_", " ")}</span></>
            )}
            {data.payments !== undefined ? (
              <>
                <span className="text-navy/50">Paid</span><span className="font-mono text-navy" data-testid="purchase-paid-amount">{formatGBP(data.paid_amount)}</span>
                {data.outstanding !== null && <><span className="text-navy/50">Still owed</span><span className="font-mono text-navy font-semibold" data-testid="purchase-still-owed">{formatGBP(data.outstanding)}</span></>}
                {data.payments.map((pm) => (
                  <span key={pm.id} className="col-span-2 text-xs text-navy/60" data-testid={`purchase-payment-${pm.id}`}>
                    <button type="button" onClick={() => navigate(`/payments/${pm.id}`)} className="underline" data-testid={`purchase-payment-link-${pm.id}`}>Payment #{pm.id}</button> · {formatGBP(pm.allocated)} {pm.method.replace("_", " ")} on {formatDateUK(pm.movement_date)} · {pm.counts ? (pm.origin === "purchase" ? "paid with invoice" : "counted") : pm.status}
                  </span>
                ))}
              </>
            ) : data.amount_paid > 0 && (
              <><span className="text-navy/50">Paid</span><span className="font-mono text-navy">{formatGBP(data.amount_paid)}</span></>
            )}
            {data.is_credit_note && (
              <><span className="text-navy/50">Type</span><span className="text-navy">Credit Note</span></>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Product details */}
      {data.product_details && (
        <Card className="border-navy/5 shadow-none">
          <CardContent className="p-4">
            <h3 className="font-semibold text-navy text-sm mb-2">Product Details</h3>
            <p className="text-sm text-navy/70 whitespace-pre-wrap">{data.product_details}</p>
          </CardContent>
        </Card>
      )}

      {data.notes && (
        <Card className="border-navy/5 shadow-none">
          <CardContent className="p-4">
            <h3 className="font-semibold text-navy text-sm mb-2">Notes</h3>
            <p className="text-sm text-navy/70">{data.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Documents — preview and download */}
      {data.documents && data.documents.length > 0 && (
        <Card className="border-navy/5 shadow-none">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold text-navy text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-gold" /> Supporting Documents ({data.documents.length})
            </h3>
            {data.documents.map((doc) => (
              <DocumentPreview key={doc.id} doc={doc} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <RecordFlags data={data} entity="purchases" />
      <ReviewPanel entity="purchases" data={data} onDone={() => setReload((n) => n + 1)} />
      <AuditTimeline history={data.history} />
      <div className="text-xs text-navy/30 space-y-1 pb-4">
        {data.created_at && <p>Created: {formatDateUK(data.created_at)}</p>}
        {data.updated_at && <p>Last updated: {formatDateUK(data.updated_at)}</p>}
        {data.approved_by && <p data-testid="approved-by">{data.status === "rejected" ? "Reviewed" : "Approved"} by: {data.approved_by_name || "Manager"}</p>}
      </div>
    </div>
  );
}

